import { ClipboardList, History, MessageSquare, PhoneIncoming, PhoneOutgoing, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { api } from "@/api";
import { CallBody } from "@/components/pipeline/LeadTimeline";
import { frDateTime } from "@/components/prospect/common";

const TABS = [["all", "Tout"], ["call", "Appels Ringover"], ["nocrm", "noCRM"], ["script", "Script"]];

const NocrmItem = ({ c }) => (
  <>
    <p className="pl-tl-head">
      {c.activity && <span className="ps-nocrm-act" style={{ "--c": c.activity_color || "#64748b" }}>{c.activity}</span>}
      <strong>{c.user_name || "noCRM"}</strong> <span className="pl-muted">· {frDateTime(c.created_at)}{c.duration ? ` · ${Math.round(c.duration)} s` : ""} · {c.sheet_title}</span>
    </p>
    {c.content && <p className="ps-nocrm-text">{c.content}</p>}
    {c.record_link && <audio className="pl-audio" controls preload="none" src={c.record_link} />}
  </>
);

const ScriptItem = ({ s }) => (
  <>
    <p className="pl-tl-head"><strong>Script « {s.script_name} »</strong> <span className="pl-muted">· {frDateTime(s.at)}</span></p>
    <ul className="ps-tl-answers">{s.answers.map((a) => <li key={a.question}><span>{a.question}</span><strong>{a.answer}</strong></li>)}</ul>
  </>
);

export function ContactTimeline({ fileId, row, onApplyDate }) {
  const [calls, setCalls] = useState(null);
  const [nocrm, setNocrm] = useState({ rows: [], comments: [] });
  const [tab, setTab] = useState("all");
  const base = `/prospect/files/${fileId}/rows/${row.id}`;
  const load = useCallback(() => {
    api.get(`${base}/calls`).then(({ data }) => setCalls(data)).catch(() => setCalls([]));
    api.get(`${base}/nocrm-history`).then(({ data }) => setNocrm(data)).catch(() => {});
  }, [base]);
  useEffect(() => { setCalls(null); setNocrm({ rows: [], comments: [] }); load(); }, [load]);
  const items = [
    ...(calls || []).map((c) => ({ type: "call", at: c.at, key: `c-${c.call_id}`, c })),
    ...nocrm.comments.map((c) => ({ type: "nocrm", at: c.created_at, key: `n-${c.id}`, c })),
    ...(row.script_answers?.answers?.length ? [{ type: "script", at: row.script_answers.at, key: "script", s: row.script_answers }] : []),
  ].filter((i) => tab === "all" || i.type === tab).sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  const icon = (i) => (i.type === "script" ? ClipboardList : i.type === "nocrm" ? (i.c.kind === "phone" ? History : MessageSquare) : i.c.kind === "call_in" ? PhoneIncoming : PhoneOutgoing);
  return (
    <section className="ps-calls" data-testid="contact-timeline">
      <div className="ps-calls-head">
        <h3>Historique du contact</h3>
        <button type="button" className="pl-link" onClick={load} data-testid="contact-timeline-refresh"><RefreshCw size={13} /> Actualiser</button>
      </div>
      {nocrm.rows.length > 0 && (
        <div className="ps-nocrm-lists" data-testid="contact-nocrm-lists">
          {nocrm.rows.map((r) => <span key={r.row_id} className={`ps-nocrm-list${r.is_active === false ? " off" : ""}`}>{r.sheet_title}{r.lead_id ? <a href={`/pipeline?lead=${r.lead_id}`}> · opportunité</a> : r.is_active === false ? " · retiré" : ""}</span>)}
        </div>
      )}
      <div className="pl-tabs" role="tablist">{TABS.map(([k, l]) => <button key={k} type="button" className={tab === k ? "on" : ""} onClick={() => setTab(k)} data-testid={`contact-timeline-tab-${k}`}>{l}</button>)}</div>
      {calls === null && <p className="pl-muted">Chargement…</p>}
      {calls !== null && !items.length && <p className="ps-calls-empty" data-testid="contact-timeline-empty">Aucun historique pour ce contact : ni appel Ringover, ni activité dans vos listes noCRM, ni script rempli.</p>}
      <ol className="pl-timeline">
        {items.map((i) => {
          const Icon = icon(i);
          return (
            <li key={i.key} className={`k-${i.type}`} data-testid={`contact-tl-${i.key}`}>
              <span className="pl-tl-icon"><Icon size={13} /></span>
              <div>
                {i.type === "call" && <><p className="pl-tl-head"><strong>{i.c.title}</strong> <span className="pl-muted">· {frDateTime(i.c.at)}</span></p>{i.c.who && <p className="pl-muted">{i.c.who}</p>}<CallBody item={i.c} base={`${base}/calls/${i.c.call_id}`} onApplyDate={onApplyDate} /></>}
                {i.type === "nocrm" && <NocrmItem c={i.c} />}
                {i.type === "script" && <ScriptItem s={i.s} />}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
